import axios from "axios";
import { logout } from "../utils/auth";

// Environment-based configuration
const getBaseURL = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  const isLocalHost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  if (isLocalHost || import.meta.env.DEV) {
    return "http://localhost:8002/api";
  }

  return "https://icd-backend.vercel.app/api";
};

// Secure token retrieval with fallback
const getAuthToken = () => {
  try {
    const token = localStorage.getItem("token");
    return token && token.trim() !== "" ? token : null;
  } catch (error) {
    console.error("Error retrieving auth token:", error);
    return null;
  }
};

export const api = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  timeout: 30000, // 30 second timeout
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor to add auth token dynamically
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers.token = token; // Keep existing token header for backward compatibility
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      logout();
    }
    return Promise.reject(error);
  }
);

export const apiForImage = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  timeout: 60000, // 60 second timeout for file uploads
  headers: {
    "Content-Type": "multipart/form-data",
    Accept: "application/json",
  },
});

// Request interceptor for image API
apiForImage.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers.token = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for image API
apiForImage.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      logout();
    }
    return Promise.reject(error);
  }
);

// User Requests
export const SignUpApi = (payload) => api.post("/auth/register", payload);
export const GetLoanAccountUsersApi = () => api.get("/auth/loan-account-users");
export const CreateLoanAccountUserApi = (payload) =>
  api.post("/auth/loan-account-user", payload);
export const UpdateLoanAccountUserApi = (id, payload) =>
  api.patch("/auth/loan-account-user/" + id, payload);
export const LoanAccountUserPasswordApi = (id, payload) =>
  api.patch("/auth/loan-account-user/" + id + "/password", payload);
export const SignInApi = (payload) => api.post("/auth/login", payload);
export const BranchesApi = () => api.get("/auth/branches");
export const BranchesPasswordChangesApi = (id, payload) =>
  api.patch("/auth/branch/change-password/" + id, payload);
// Dashboard Requests
export const GetDashboardDataApi = () => api.get("/dashboard/cards-data");

// Product Requests
// export const CreateProductApi = (payload) => api.post("/item", payload);
export const EditProductApi = (id, payload) =>
  api.patch("/item/" + id, payload);
export const AddItemOpeningQtyApi = (id, payload) =>
  api.patch("/item/opening-qty/" + id, payload);
export const DeleteProductsByIdApi = (id) => api.delete("/item/" + id);
export const GetAllProductsApi = () => api.get("/item");
export const GetProductsByIdApi = (id) => api.get("/item/" + id);
export const GetItemSummaryApi = (id) => api.get("/item/summary/" + id);

// Labour Requests
export const CreateLabourApi = (payload) => api.post("/labour", payload);
export const EditLabourApi = (id, payload) =>
  api.patch("/labour/" + id, payload);
export const DeleteLaboursByIdApi = (id) => api.delete("/labour/" + id);
export const GetAllLaboursApi = () => api.get("/labour");
export const GetLaboursByIdApi = (id) => api.get("/labour/" + id);

// Stock Stats Requests
export const AddStockStatsApi = (payload) => api.post("/stock", payload);
export const GetStockStatsApi = () => api.get("/stock");
// Expense Requests
export const CreateExpenseApi = (payload) => api.post("/expense", payload);
export const UpdateExpenseApi = (id, payload) =>
  api.patch("/expense/" + id, payload);
export const DeleteExpenseApi = (id) => api.delete("/expense/" + id);
export const GetExpenseApi = () => api.get("/expense");
export const GetExpenseByIdApi = (id) => api.get("/expense/" + id);

export const GetExpenseTypesApi = (branchId) =>
  api.get("/expense-type", { params: branchId ? { branchId } : {} });
export const CreateExpenseTypeApi = (payload) =>
  api.post("/expense-type", payload);
export const UpdateExpenseTypeApi = (id, payload) =>
  api.patch("/expense-type/" + id, payload);
export const DeleteExpenseTypeApi = (id) =>
  api.delete("/expense-type/" + id);

export const CreateLoanApi = (payload) => api.post("/loan", payload);
export const GetLoanApi = () => api.get("/loan");
export const GetMyLoanApi = () => api.get("/loan/me");
export const GetLoansByBranchApi = (branchId) =>
  api.get("/loan/branch/" + branchId);
export const GetLoanLogApi = () => api.get("/loan/log");
export const GetLoanLogByBranchApi = (branchId) =>
  api.get("/loan/log/branch/" + branchId);
export const GetLoanByIdApi = (id) => api.get("/loan/" + id);
export const UpdateLoanApi = (id, payload) => api.patch("/loan/" + id, payload);
export const UpdateLoanDisbursementApi = (id, payload) =>
  api.patch("/loan/disbursement/" + id, payload);
export const DeleteLoanApi = (id) => api.delete("/loan/" + id);
export const CreateLoanPaymentApi = (loanId, payload) =>
  api.post("/loan/" + loanId + "/payment", payload);
export const UpdateLoanPaymentApi = (paymentId, payload) =>
  api.patch("/loan/payment/" + paymentId, payload);
export const DeleteLoanPaymentApi = (paymentId) =>
  api.delete("/loan/payment/" + paymentId);

// Supplier Invoice Requests
export const CreateSupplierInvoiceApi = (payload) =>
  api.post("/supplier-invoice", payload);
export const GetSupplierInvoiceApi = () => api.get("/supplier-invoice");
// Customer Invoice Requests
export const CreateCustomerInvoiceApi = (payload) =>
  api.post("/customer-invoice", payload);
export const UpdateInvoiceItemApi = (id, payload) =>
  api.patch("/customer-invoice/item/" + id, payload);
export const GetCustomerInvoiceApi = () => api.get("/customer-invoices");
export const GetCustomerInvoiceByIdApi = (id) =>
  api.get("/customer-invoice/" + id);
export const GetGuestCustomerInvoicesApi = (params) =>
  api.get("/customer-invoice/guest/list", { params });
export const GetCustomerInvoiceSingleApi = (id) =>
  api.get("/customer-invoice/single/" + id);
export const UpdateCustomerInvoiceDetailsApi = (id, payload) =>
  api.patch("/customer-invoice/" + id + "/details", payload);
export const createNewItemInvoiceApi = (payload) =>
  api.post("/customer-invoice/add-new-item", payload);

export const DeleteInvoiceItemAPI = (id) =>
  api.post("/customer-invoice/item/" + id);
export const DeleteCustomerInvoiceApi = (id) =>
  api.delete("/customer-invoice/" + id);

// customer returns Requests
export const GetAllCustomerReturnsApi = (params) =>
  api.get("/customer-return/", { params });
export const GetCustomerReturnSingleApi = (id) =>
  api.get("/customer-return/single/" + id);
export const GetCustomerReturnInvoiceByIdApi = (id) =>
  api.get("/customer-return/single/" + id);
export const GetCustomerReturnInvoicesApi = (id) =>
  api.get("/customer-return/by-customer/" + id);
export const UpdateCustomerReturnDetailsApi = (id, payload) =>
  api.patch("/customer-return/" + id + "/details", payload);
export const UpdateReturnItemApi = (id, payload) =>
  api.patch("/customer-return/item/" + id, payload);
export const DeleteReturnItemAPI = (id) =>
  api.post("/customer-return/item/" + id);
export const createNewItemReturnApi = (payload) =>
  api.post("/customer-return/add-new-item", payload);
export const DELETECustomerReturnInvoicesApi = (id) =>
  api.delete("/customer-return/" + id);

// Return Requests
export const CreateCustomerReturnApi = (payload) =>
  api.post("/customer-return", payload);

// Customer Payment Requests
export const CreateCustomerPaymentApi = (payload) =>
  api.post("/customer-payment", payload);
export const DeleteCustomerPaymentApi = (id) =>
  api.delete("/customer-payment/" + id);
export const UpdateCustomerPaymentApi = (id, payload) =>
  api.patch("/customer-payment/" + id, payload);
export const GetCustomerPaymentByIdApi = (id) =>
  api.get("/customer-payment/" + id);
export const GetCustomerPaymentsApi = () => api.get("/customer-payment");

// customer invoices request
export const CheckInvoiceNoAvailabliltyAPI = (checkinvoice) =>
  api.get("/customer-invoice/check-invoice/" + checkinvoice);
export const GetCustomerInvoicesByIdAPI = (params) =>
  api.get("/customer-invoice", { params });

// Summary Requests
export const getCustomerItemSummaryById = (id) =>
  api.get("/customer-item-summary/" + id);
export const GetCustomerItemSummaryRecordsApi = (customerId) =>
  api.get("/customer-item-summary/" + customerId + "/records");
export const CreateCustomerItemSummaryRecordApi = (payload) =>
  api.post("/customer-item-summary/records", payload);
export const GetCustomerItemSummaryRecordByIdApi = (recordId) =>
  api.get("/customer-item-summary/record/" + recordId);
export const DeleteCustomerItemSummaryRecordApi = (recordId) =>
  api.delete("/customer-item-summary/record/" + recordId);
export const GetCustomerLedgerRecordsApi = (customerId) =>
  api.get("/customer-ledger/" + customerId + "/records");
export const CreateCustomerLedgerRecordApi = (payload) =>
  api.post("/customer-ledger/records", payload);
export const GetCustomerLedgerRecordByIdApi = (recordId) =>
  api.get("/customer-ledger/record/" + recordId);
export const DeleteCustomerLedgerRecordApi = (recordId) =>
  api.delete("/customer-ledger/record/" + recordId);
export const getCompanyItemSummaryById = (id) =>
  api.get("/company-item-summary/" + id);

// Supplier Payment Requests
export const CreateSupplierPaymentApi = (payload) =>
  api.post("/supplier-payment", payload);
export const UpdateSupplierPaymentApi = (id, payload) =>
  api.patch("/supplier-payment/" + id, payload);
export const DeleteSupplierPaymentApi = (id) =>
  api.delete("/supplier-payment/" + id);
export const GetCompanyPaymentByIdApi = (id) =>
  api.get("/supplier-payment/" + id);
export const GetCompanyPaymentsApi = () => api.get("/supplier-payment");

// Customer Requests
export const GetCustomersAPI = () => api.get("/customer");
export const GetReceivablesOverviewAPI = () =>
  api.get("/customer/receivables-overview");
export const UpdateCustomersOpeningBalanceAPI = (id, payload) =>
  api.patch("/customer/opening_balance/" + id, payload);

// Accounts
export const CreateAccount = (payload) => api.post("/accounts/create", payload);

// Companies Requests
export const GetCompanyAPI = () => api.get("/company");
export const GetPayablesOverviewAPI = () => api.get("/company/payables-overview");
export const CreateCompanyAPI = (payload) => api.post("/company", payload);
export const UpdateCompanyAPI = (id, payload) =>
  api.patch("/company/" + id, payload);
export const DeleteCompanyAPI = (id) => api.delete("/company/" + id);

// Accounts Requests
export const CreateAccountAPI = (payload) =>
  api.post("/accounts/create", payload);
export const GetAccountsApi = () => api.get("/accounts");
export const GetAccountLogsApi = (id, params) =>
  api.get("/accounts/" + id + "/logs", { params });
export const UpdateAccountsApi = (id, payload) =>
  api.patch("/accounts/" + id, payload);
export const UpdateAccountsOpeningBalanceApi = (id, payload) =>
  api.post("/accounts/opening_balance/" + id, payload);
export const DeleteAccountsApi = (id) => api.post("/accounts/delete/" + id);
export const GetCashBookApi = (payload) => api.post("/cash-book", payload);
export const GetCashAdjustmentsApi = () => api.get("/cash-adjustment");
export const CreateCashAdjustmentApi = (payload) =>
  api.post("/cash-adjustment", payload);
export const DeleteCashAdjustmentApi = (id) =>
  api.delete("/cash-adjustment/" + id);

// Ledgers Requests
export const GetCustomerLedgerApi = (id, sort) =>
  api.get("/ledger/customer/" + id, {
    params: sort ? { sort } : undefined,
  });
export const GetCustomerItemLedgerApi = (id) =>
  api.get("/item-ledger/customer/" + id);
export const GetSupplierLedgerApi = (id) => api.get("/ledger/supplier/" + id);

// RawMaterial Requests
export const CreateRawMaterialApi = (payload) =>
  api.post("/raw-material", payload);
export const EditRawMaterialApi = (id, payload) =>
  api.patch("/raw-material/" + id, payload);
export const DeleteRawMaterialApi = (id) => api.delete("/raw-material/" + id);
export const GetAllRawMaterialApi = () => api.get("/raw-material");
export const GetRawMaterialByIdApi = (id) => api.get("/raw-material/" + id);
// Customer Requests
export const CreateCustomerApi = (payload) =>
  api.post("/customer/create", payload);
export const EditCustomerApi = (id, payload) =>
  api.patch("/customer/update/" + id, payload);
export const DeleteCustomerApi = (id) => api.delete("/customer/delete/" + id);
export const GetAllCustomerApi = () => api.post("/customer/all");
export const GetBranchCustomerApi = (payload) =>
  api.post("/customer/branch", payload);
export const GetCustomerByIdApi = (id) => api.get("/customer/" + id);

// Supplier Requests
export const CreateSupplierApi = (payload) =>
  api.post("/company/create", payload);
export const GetAllSupplierApi = () => api.get("/company/all");
export const GetBranchSupplierApi = (id) => api.get("/company/branch/" + id);
export const GetSupplierByIdApi = (id) => api.get("/supplier/" + id);
export const UpdateSupplierByIdApi = (id, payload) =>
  api.patch("/company/update/" + id, payload);
export const DeleteSupplierByIdApi = (id) =>
  api.delete("/company/update/" + id);

// Stock Stats Requests
export const GetBranchStocksStatsApi = (id) =>
  api.get("/stock/stats-branch/" + id);
export const GetAllStocksStatsApi = () => api.get("/stock/stats-admin");

// Category Requests
export const GetAllCategoriesAPI = () => api.get("/category/all");
export const GetBranchCategoriesAPI = (id) => api.get("/category/branch/" + id);

// Sub Category Requests
export const GetAllSubCategoriesAPI = () => api.get("/sub-category/all");
export const GetBranchSubCategoriesAPI = (id) =>
  api.get("/sub-category/branch/" + id);

// Items Requests
export const CreateProductApi = (data) => api.post("/item", data);
export const UpdateProductApi = (id, data) => api.patch("/item/" + id, data);
export const GetAllItemsAPI = () => api.get("/item");
export const GetBranchItemsAPI = (id) => api.get("/item/branch/" + id);
export const AdjustItemQtyApi = (id, payload) =>
  api.patch("/item/" + id + "/adjust-qty", payload);
export const GetItemQtyLogsApi = (id) => api.get("/item/" + id + "/qty-logs");
export const GetAllItemQtyAdjustmentLogsApi = (params) =>
  api.get("/item/qty-adjustment-logs", { params });

// Company Invoice Requests
export const CreateCompanyInvoiceApi = (data) =>
  api.post("/company-invoice", data);
export const CreateManyCompanyInvoiceApi = (data) =>
  api.post("/company-invoice/many", data);
export const UpdateCompanyInvoiceApi = (id, data) =>
  api.patch("/company-invoice/" + id, data);
export const DeleteCompanyInvoiceApi = (id) =>
  api.delete("/company-invoice/" + id);
export const GetCompanyInvoiceByIdAPI = (id) =>
  api.get("/company-invoice/" + id);
export const GetCompanyInvoiceAPI = () => api.get("/company-invoice");

export const GetBookByIdApi = (id) => api.get("/books/all/" + id);
export const GetBookApi = () => api.get("/books/all");
export const EditBookApi = (id, payload) =>
  apiForImage.patch("/books/update/" + id, payload);
export const DeleteBookApi = (id) => api.delete("/books/delete/" + id);
export const UpdateBookApi = (id, chap) =>
  api.patch("/books/edit-add-chapter/" + id, chap);
export const DeleteChapterBookApi = (id, payload) =>
  api.patch("/books/edit-delete-chapter/" + id, payload);
export const AddCustomerToBookApi = (payload) =>
  api.patch("/books/add-customer", payload);
export const DelCustomerToBookApi = (payload) =>
  api.patch("/books/remove-customer", payload);
export const ChangeBookVisibilityApi = (payload) =>
  api.patch("/books/update-visibilty", payload);

// Investor Requests
export const CreateInvestorApi = (payload) => api.post("/investor", payload);
export const GetAllInvestorsApi = () => api.get("/investor");
export const GetInvestorByIdApi = (id) => api.get("/investor/" + id);
export const EditInvestorApi = (id, payload) =>
  api.patch("/investor/" + id, payload);
export const AddInvestmentApi = (id, payload) =>
  api.patch("/investor/add-investment/" + id, payload);
export const GetInvestmentApi = (id) =>
  api.get("/investor/get-investments/" + id);
export const DeleteInvestorApi = (id) => api.delete("/investor/" + id);

// Health (internet / server reachability)
export const HealthCheckApi = () =>
  api.get("/health", { timeout: 8000 });

// Reports
export const GetReportApi = (payload, config = {}) =>
  api.post("/daily-report", payload, {
    timeout: 120000, // year / wide ranges need longer than default 30s
    ...config,
  });

export const FinalizeDayCloseApi = (payload) =>
  api.post("/day-close/finalize", payload);
export const GetDayCloseApi = (params) =>
  api.get("/day-close", { params });

// Direct discounts (Customer user_type 1, Company user_type 2)
export const CreateDirectDiscountApi = (payload) =>
  api.post("/discounts", payload);
export const GetDiscountsByUserApi = (type, id) =>
  api.get("/discounts/user/" + type + "/" + id);
export const GetAllDiscountsApi = () => api.get("/discounts");
export const UpdateDirectDiscountApi = (id, payload) =>
  api.patch("/discounts/" + id, payload);
export const DeleteDirectDiscountApi = (id) => api.delete("/discounts/" + id);

// Non-product invoices (Customer user_type 1, Company user_type 2)
export const CreateNonProductInvoiceApi = (payload) =>
  api.post("/non-product-invoice", payload);
export const GetNonProductInvoicesApi = () => api.get("/non-product-invoice");
export const GetNonProductInvoicesByUserApi = (type, id) =>
  api.get("/non-product-invoice/user/" + type + "/" + id);
export const UpdateNonProductInvoiceApi = (id, payload) =>
  api.patch("/non-product-invoice/" + id, payload);
export const DeleteNonProductInvoiceApi = (id) =>
  api.delete("/non-product-invoice/" + id);
